import dayjs from 'dayjs';
import { atom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { atomEffect } from 'jotai-effect';
import type { ApplicationLocale } from 'types';

import en from '../source/en.i18n.json';
import fr from '../source/fr.i18n.json';
import nl from '../source/nl.i18n.json';

import type {
  LocaleTree,
  LooseTranslateFunction,
  TranslateFunction,
  TranslationState,
} from './types';
import { getPreferredBrowserLanguageOrDefault, getTranslationForKey } from './utils';

export * from './types';

export const currentLanguageAtom = atomWithStorage<ApplicationLocale>(
  'lastUsedLanguage',
  getPreferredBrowserLanguageOrDefault(),
);

const propagateLanguageChangeEffect = atomEffect(get => {
  const currentLanguage = get(currentLanguageAtom);
  dayjs.locale(currentLanguage);
});

const translationsObjectAtom = atom<LocaleTree>(get => {
  get(propagateLanguageChangeEffect);
  const currentLanguage = get(currentLanguageAtom);
  return loadLanguage(currentLanguage);
});

const translateFunctionAtom = atom<TranslateFunction>(get => {
  const translationsObject = get(translationsObjectAtom);
  const currentLanguage = get(currentLanguageAtom);

  return (key, params) => getTranslationForKey(key, params, translationsObject, currentLanguage);
});

export const translationStateAtom = atom<TranslationState>(get => {
  const currentLang = get(currentLanguageAtom);
  const t = get(translateFunctionAtom);

  //? `tl`: "Translate Loose", see type declaration for details
  return { currentLang, t, tl: t as LooseTranslateFunction };
});

export function useTranslation() {
  const translationState = useAtomValue(translationStateAtom);
  return translationState;
}

export function useTranslate() {
  const { t } = useTranslation();
  return t;
}

const languages = {
  en,
  fr,
  nl,
};

function loadLanguage(language: ApplicationLocale): LocaleTree {
  // eslint-disable-next-line global-require, import/no-dynamic-require
  return languages[language] as LocaleTree;
}
